import { backend } from "../../../../../xconvert-backend";
import { authentication } from "../../../../../authentication";

export interface AiParseResult {
    _id: string;
    input: string;
    output: string;
    type: string;
    timestamp: string;
    model: string;
    tokensConsumed: number;
    totalPrice: number;
    counter: number;
    rawOutput?: string;
    promptTokens?: number;
    completionTokens?: number;
    thinkingMessage?: string;
}

export interface SingleRunResult {
    output: string;
    thinkingMessage?: string;
    promptTokens?: number;
    completionTokens?: number;
    price?: number;
}

export interface AiRunOptions {
    disableCache?: boolean;
}

export interface PrettifyAiOutputResponse {
    prettified: string;
    compact: string | null;
    isJson: boolean;
}


export async function prettifyAiOutput(text: string): Promise<PrettifyAiOutputResponse> {
    if (!text || !text.trim()) {
        return { prettified: text || "", compact: null, isJson: false };
    }

    const response = await fetch("/converter/v1/prettifyAiOutput", {
        method: "POST",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({ text })
    });

    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || `Server error (${response.status})`);
    }

    return response.json();
}

export async function runSingleAiRequest(
    input: string,
    model: string,
    options?: AiRunOptions
): Promise<SingleRunResult> {
    const messages = [{ role: 'user', content: input }];
    const conversationJson = JSON.stringify(messages);
    const response = await continueAiChat(conversationJson, model, options);
    return {
        output: response.output,
        thinkingMessage: response.thinkingMessage,
        promptTokens: response.usageTokensNumber,
        completionTokens: response.completionTokensNumber,
        price: response.totalPromptPrice
    };
}


export async function fetchAiParseResults(params: {
    _id?: string;
    type?: string;
    inputContains?: string;
    outputContains?: string;
    limit?: number;
    hasThinking?: boolean;
}): Promise<AiParseResult[]> {
    const auth = await backend.withTokenAuthHeader(authentication.token);

    const requestHeaders: Record<string, string> = {
        "Content-Type": "application/json; charset=utf-8",
    };
    Object.assign(requestHeaders, auth.headers);

    const response = await fetch('/api/internal/getAiParseResults', {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({
            _id: params._id || null,
            type: params.type || null,
            inputContains: params.inputContains || null,
            outputContains: params.outputContains || null,
            limit: params.limit || 50,
            hasThinking: params.hasThinking ?? null
        })
    });

    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || `Server error (${response.status})`);
    }

    const data = await response.json();
    return data.results;
}


export async function continueAiChat(
    conversationHistory: string,
    model: string,
    options?: AiRunOptions
): Promise<any> {
    const response = await fetch("/converter/v1/aiChat", {
        method: "POST",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
            input: conversationHistory,
            rawInput: conversationHistory,
            model: model,
            type: "ai_chat",
            temperature: 0.0,
            callTimeout: 300,
            aiProcessingSettings: options?.disableCache ? { disableAiRequestsCache: true } : null
        })
    });

    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || `Server error (${response.status})`);
    }

    return response.json();
}


export async function fetchTransportOrderById(
    transportOrderId: string
): Promise<any> {
    const auth = await backend.withTokenAuthHeader(authentication.token);

    const requestHeaders: Record<string, string> = {
        "Content-Type": "application/json; charset=utf-8",
    };
    Object.assign(requestHeaders, auth.headers);

    return backend.transportOrderApi.fetchTransportOrderById(transportOrderId, auth);
}

export function extractDatabaseEntryIds(transportOrder: any): string[] {
    const ids = new Set<string>();

    if (!transportOrder?.additionalData) return [];

    transportOrder.additionalData.forEach((item: any) => {
        const key = item.key;
        const value = item.value;

        if (key === 'databaseEntryIDs' && value) {
            const matches = value.matchAll(/:\s*([a-f0-9]{32})/g);
            for (const match of matches) {
                ids.add(match[1]);
            }
        } else if (key.startsWith('DataBaseEntryId')) {
            if (value) ids.add(value);
        }
    });

    return Array.from(ids);
}

export async function fetchAiRequestsByIds(ids: string[]): Promise<AiParseResult[]> {
    const results: AiParseResult[] = [];

    for (const id of ids) {
        try {
            const data = await fetchAiParseResults({ _id: id, limit: 1 });
            if (data.length > 0) results.push(data[0]);
        } catch (e) {
            console.error(`Failed to fetch AI request ${id}:`, e);
        }
    }

    return results;
}